import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  uploadPatientDocuments,
  deletePatientDocument,
} from '../features/patients/patientSlice';
import {
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: theme.palette.background.paper,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
}));

const DocumentIcon = ({ type }) => {
  switch (type) {
    case 'image':
      return <ImageIcon />;
    case 'pdf':
      return <PdfIcon />;
    default:
      return <DescriptionIcon />;
  }
};

const PatientDocuments = ({ patientId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { documents } = useSelector((state) => state.patients);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('type', documentType);
    formData.append('description', description);

    try {
      await dispatch(uploadPatientDocuments({ patientId, formData })).unwrap();
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setDocumentType('');
      setDescription('');
    } catch (error) {
      console.error('Failed to upload documents:', error);
    }
  };

  const handleDelete = async (documentId) => {
    try {
      await dispatch(deletePatientDocument({ patientId, documentId })).unwrap();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'document';
  };

  return (
    <StyledPaper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h5'>Patient Documents</Typography>
        <Button
          variant='contained'
          startIcon={<UploadIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          Upload Documents
        </Button>
      </Box>

      {documents.error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {documents.error}
        </Alert>
      )}

      {documents.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : documents.data.length === 0 ? (
        <Alert severity='info'>
          No documents uploaded yet. Click the upload button to add documents.
        </Alert>
      ) : (
        <List>
          {documents.data.map((doc) => (
            <ListItem
              key={doc.id}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <DocumentIcon type={getFileType(doc.fileName)} />
              <ListItemText
                primary={doc.fileName}
                secondary={doc.description || doc.type}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge='end'
                  aria-label='delete'
                  onClick={() => handleDelete(doc.id)}
                  color='error'
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Document Type'
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                helperText='e.g., Medical Records, Insurance Card, ID Proof'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                helperText='Add any relevant details about the documents'
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant='outlined'
                component='label'
                fullWidth
                startIcon={<UploadIcon />}
              >
                Select Files
                <input
                  type='file'
                  hidden
                  multiple
                  onChange={handleFileSelect}
                  accept='.pdf,.jpg,.jpeg,.png,.gif'
                />
              </Button>
            </Grid>
            {selectedFiles.length > 0 && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom>
                  Selected Files:
                </Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={file.name} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant='contained'
            disabled={selectedFiles.length === 0 || documents.loading}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            {documents.loading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Upload'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
};

export default PatientDocuments;
